/**
 * Dashboard Check-In Tab Controller
 * Real-time check-in status with Firestore onSnapshot
 */
class DashboardCheckin {
    static TRANSLATIONS = {
        en: {
            failedToLoad: 'Failed to load check-in data',
            noGuestsMatch: 'No guests match your search',
            noAttendingGuests: 'No attending guests found',
            sortName: "Name A–Z", sortOut: "Not yet first", sortIn: "Checked in first", sortRecent: "Latest arrivals first", sortLabel: "Sort guests",
            guest: 'Guest',
            checkedIn: 'Checked In',
            pending: 'Pending',
            partialTitle: 'Only some of this party has checked in',
            masterQrTitle: 'Master Check-In Sign',
            masterQrDesc: 'Print this QR code and place it at the entrance. Guests scan it to find their name and table.',
            masterQrOpen: 'Open Guest Check-In Link',
            masterQrDownload: 'Download image',
            undo: 'Undo',
            undoConfirm: 'Undo check-in for',
            undoFailed: 'Could not undo the check-in',
            upgradeTitle: 'Unlock QR check-in',
            upgradeDesc: 'Upgrade this event to check guests in at the door with QR codes — arrivals update live on every device.',
            upgradeCta: 'Upgrade Now',
            resendTitle: 'Resend QR Pass',
            resendBtnTitle: 'Resend QR pass',
            resendFree: 'Free — uses no messaging credits.',
            resendEmailBtn: 'Email QR Pass',
            resendSendingTo: 'Sending to:',
            resendNoEmail: 'No email on file for this guest — share the pass link instead.',
            resendAddEmailHint: 'No email on file — add one here and we\'ll send the pass and keep it on their record.',
            resendAddEmailPlaceholder: 'guest@example.com',
            resendAddEmailBtn: 'Save & email the pass',
            resendEmailSaved: 'Email saved',
            resendEmailInvalid: 'That does not look like an email address.',
            resendWhatsAppBtn: 'Send via WhatsApp',
            resendCopyBtn: 'Copy pass link',
            resendCopied: 'Pass link copied',
            resendSent: 'Pass emailed',
            resendFailed: 'Could not send the pass',
            resendWhatsAppMsg: 'Here is your check-in QR pass for',
            resendAllBtn: 'Resend QR Passes',
            resendAllConfirm: 'Email every attending guest their personal QR pass again? Guests without an email address are skipped. This is free and uses no messaging credits.',
            resendAllDone: 'Passes emailed',
            resendAllNoEmail: 'no email',
            resendAllNone: 'No attending guests have an email address on file',
            privacyLabel: 'Privacy mode for self-serve check-in',
            privacyDesc: 'The Master QR displays a search box and hides the guest list.',
            privacySaved: 'Privacy mode updated',
            privacySaveFailed: 'Could not save the setting',
            statCheckedIn: 'Checked in',
            statNotYet: 'Not yet',
            statExpected: 'Expected',
            statRate: 'Rate',
            progressLabel: 'Check-in progress',
            progressOf: '{n} of {total}',
            statWalkIns: 'Walk-ins',
            walkInChip: 'Walk-in',
            qaTitle: 'QR Auto seating is on, but there are no tables',
            qaBody: 'Guests who check in will not be given a table. Add tables on the Seating tab first, or open it anyway.',
            qaAdd: 'Add tables',
            qaOpen: 'Open anyway',
            qaCancel: 'Cancel',
            qaMute: 'Don\'t warn me again for this event',
            tableNone: 'No table',
            tableChange: 'Change table',
            tableMoveTo: 'Move {name} to',
            tablePartyOf: '(party of {n})',
            tableFullMark: 'full',
            tableFullConfirm: '{table} has {free} free seat(s) and this party needs {n}. Seat them there anyway?',
            tableMoveFailed: 'Could not change the table'
        },
        es: {
            failedToLoad: 'Error al cargar datos de check-in',
            noGuestsMatch: 'Ningún invitado coincide con tu búsqueda',
            noAttendingGuests: 'No se encontraron invitados confirmados',
            sortName: "Nombre A–Z", sortOut: "Sin registrar primero", sortIn: "Registrados primero", sortRecent: "Últimas llegadas primero", sortLabel: "Ordenar invitados",
            guest: 'Invitado',
            checkedIn: 'Registrado',
            pending: 'Pendiente',
            partialTitle: 'Solo algunas personas de este grupo se registraron',
            masterQrTitle: 'Cartel QR de entrada',
            masterQrDesc: 'Imprime este QR y ponlo en la entrada. Los invitados lo escanean para encontrar su nombre y mesa.',
            masterQrOpen: 'Abrir enlace de entrada',
            masterQrDownload: 'Descargar imagen',
            undo: 'Deshacer',
            undoConfirm: 'Deshacer el registro de',
            undoFailed: 'No se pudo deshacer el registro',
            upgradeTitle: 'Desbloquea el check-in con QR',
            upgradeDesc: 'Actualiza este evento para registrar a los invitados en la entrada con códigos QR: las llegadas se actualizan en vivo en todos los dispositivos.',
            upgradeCta: 'Actualizar Ahora',
            resendTitle: 'Reenviar pase QR',
            resendBtnTitle: 'Reenviar pase QR',
            resendFree: 'Gratis — no usa créditos de mensajería.',
            resendEmailBtn: 'Enviar el pase QR',
            resendSendingTo: 'Se enviará a:',
            resendNoEmail: 'Este invitado no tiene email registrado — comparte el enlace del pase.',
            resendAddEmailHint: 'Sin email registrado: añade uno aquí y enviaremos el pase y lo guardaremos en su ficha.',
            resendAddEmailPlaceholder: 'invitado@ejemplo.com',
            resendAddEmailBtn: 'Guardar y enviar el pase',
            resendEmailSaved: 'Email guardado',
            resendEmailInvalid: 'Eso no parece una dirección de email.',
            resendWhatsAppBtn: 'Enviar por WhatsApp',
            resendCopyBtn: 'Copiar enlace del pase',
            resendCopied: 'Enlace copiado',
            resendSent: 'Pase enviado por email',
            resendFailed: 'No se pudo enviar el pase',
            resendWhatsAppMsg: 'Aquí está tu pase QR de check-in para',
            resendAllBtn: 'Reenviar pases QR',
            resendAllConfirm: '¿Enviar de nuevo por email a cada invitado confirmado su pase QR personal? Se omiten los invitados sin email. Es gratis y no usa créditos de mensajería.',
            resendAllDone: 'Pases enviados',
            resendAllNoEmail: 'sin email',
            resendAllNone: 'Ningún invitado confirmado tiene email registrado',
            privacyLabel: 'Modo de privacidad para el check-in autónomo',
            privacyDesc: 'El QR general muestra un buscador y oculta la lista de invitados.',
            privacySaved: 'Modo de privacidad actualizado',
            privacySaveFailed: 'No se pudo guardar el ajuste',
            statCheckedIn: 'Registrados',
            statNotYet: 'Aún no',
            statExpected: 'Esperados',
            statRate: 'Porcentaje',
            progressLabel: 'Progreso del check-in',
            progressOf: '{n} de {total}',
            statWalkIns: 'Sin reserva',
            walkInChip: 'Sin reserva',
            qaTitle: 'La asignación automática por QR está activa, pero no hay mesas',
            qaBody: 'Los invitados que hagan check-in no recibirán mesa. Añade mesas primero en la pestaña Asientos, o ábrelo de todos modos.',
            qaAdd: 'Añadir mesas',
            qaOpen: 'Abrir de todos modos',
            qaCancel: 'Cancelar',
            qaMute: 'No volver a avisar en este evento',
            tableNone: 'Sin mesa',
            tableChange: 'Cambiar mesa',
            tableMoveTo: 'Mover a {name} a',
            tablePartyOf: '(grupo de {n})',
            tableFullMark: 'llena',
            tableFullConfirm: '{table} tiene {free} asiento(s) libre(s) y este grupo necesita {n}. ¿Sentarlos allí de todos modos?',
            tableMoveFailed: 'No se pudo cambiar la mesa'
        },
        fr: {
            failedToLoad: 'Échec du chargement des données de check-in',
            noGuestsMatch: 'Aucun invité ne correspond à votre recherche',
            noAttendingGuests: 'Aucun invité confirmé trouvé',
            sortName: "Nom A–Z", sortOut: "Pas encore arrivés d’abord", sortIn: "Enregistrés d’abord", sortRecent: "Dernières arrivées d’abord", sortLabel: "Trier les invités",
            guest: 'Invité',
            checkedIn: 'Enregistré',
            pending: 'En attente',
            partialTitle: 'Seule une partie de ce groupe est enregistrée',
            masterQrTitle: "Panneau QR d'entrée",
            masterQrDesc: "Imprimez ce QR et placez-le à l'entrée. Les invités le scannent pour trouver leur nom et table.",
            masterQrOpen: "Ouvrir le lien d'entrée",
            masterQrDownload: "Télécharger l'image",
            undo: 'Annuler',
            undoConfirm: "Annuler l'enregistrement de",
            undoFailed: "Impossible d'annuler l'enregistrement",
            upgradeTitle: 'Débloquez le check-in par QR',
            upgradeDesc: 'Passez à la version supérieure pour enregistrer vos invités à l’entrée avec des QR codes — les arrivées se mettent à jour en direct sur tous les appareils.',
            upgradeCta: 'Mettre à niveau',
            resendTitle: 'Renvoyer le pass QR',
            resendBtnTitle: 'Renvoyer le pass QR',
            resendFree: 'Gratuit — n’utilise aucun crédit de messagerie.',
            resendEmailBtn: 'Envoyer le pass QR',
            resendSendingTo: 'Envoi à :',
            resendNoEmail: 'Aucun e-mail enregistré pour cet invité — partagez le lien du pass.',
            resendAddEmailHint: 'Aucun email enregistré : ajoutez-en un ici, nous enverrons le pass et le garderons dans sa fiche.',
            resendAddEmailPlaceholder: 'invite@exemple.com',
            resendAddEmailBtn: 'Enregistrer et envoyer le pass',
            resendEmailSaved: 'Email enregistré',
            resendEmailInvalid: 'Cela ne ressemble pas à une adresse email.',
            resendWhatsAppBtn: 'Envoyer par WhatsApp',
            resendCopyBtn: 'Copier le lien du pass',
            resendCopied: 'Lien copié',
            resendSent: 'Pass envoyé par e-mail',
            resendFailed: 'Impossible d’envoyer le pass',
            resendWhatsAppMsg: 'Voici votre pass QR de check-in pour',
            resendAllBtn: 'Renvoyer les pass QR',
            resendAllConfirm: 'Renvoyer par e-mail à chaque invité confirmé son pass QR personnel ? Les invités sans e-mail sont ignorés. C’est gratuit et n’utilise aucun crédit de messagerie.',
            resendAllDone: 'Pass envoyés',
            resendAllNoEmail: 'sans e-mail',
            resendAllNone: 'Aucun invité confirmé n’a d’adresse e-mail enregistrée',
            privacyLabel: 'Mode confidentialité pour l’enregistrement en libre-service',
            privacyDesc: 'Le QR principal affiche une barre de recherche et masque la liste des invités.',
            privacySaved: 'Mode confidentialité mis à jour',
            privacySaveFailed: 'Impossible d’enregistrer le réglage',
            statCheckedIn: 'Enregistrés',
            statNotYet: 'Pas encore',
            statExpected: 'Attendus',
            statRate: 'Taux',
            progressLabel: 'Progression des arrivées',
            progressOf: '{n} sur {total}',
            statWalkIns: 'Sans inscription',
            walkInChip: 'Sans inscription',
            qaTitle: 'Le placement auto par QR est activé, mais il n’y a aucune table',
            qaBody: 'Les invités qui s’enregistrent n’auront pas de table. Ajoutez d’abord des tables dans l’onglet Plan de table, ou ouvrez quand même.',
            qaAdd: 'Ajouter des tables',
            qaOpen: 'Ouvrir quand même',
            qaCancel: 'Annuler',
            qaMute: 'Ne plus m’avertir pour cet événement',
            tableNone: 'Sans table',
            tableChange: 'Changer de table',
            tableMoveTo: 'Déplacer {name} vers',
            tablePartyOf: '(groupe de {n})',
            tableFullMark: 'complète',
            tableFullConfirm: '{table} a {free} place(s) libre(s) et ce groupe en demande {n}. Les placer là quand même ?',
            tableMoveFailed: 'Impossible de changer la table'
        },
        pt: {
            failedToLoad: 'Falha ao carregar dados de check-in',
            noGuestsMatch: 'Nenhum convidado corresponde à sua busca',
            noAttendingGuests: 'Nenhum convidado confirmado encontrado',
            sortName: "Nome A–Z", sortOut: "Não registrados primeiro", sortIn: "Registrados primeiro", sortRecent: "Chegadas recentes primeiro", sortLabel: "Ordenar convidados",
            guest: 'Convidado',
            checkedIn: 'Registrado',
            pending: 'Pendente',
            partialTitle: 'Apenas parte deste grupo fez check-in',
            masterQrTitle: 'QR Code de entrada',
            masterQrDesc: 'Imprima este QR e coloque na entrada. Os convidados escaneiam para encontrar seu nome e mesa.',
            masterQrOpen: 'Abrir link de entrada',
            masterQrDownload: 'Baixar imagem',
            undo: 'Desfazer',
            undoConfirm: 'Desfazer o check-in de',
            undoFailed: 'Não foi possível desfazer o check-in',
            upgradeTitle: 'Desbloqueie o check-in por QR',
            upgradeDesc: 'Atualize este evento para registar os convidados à entrada com códigos QR — as chegadas atualizam-se em direto em todos os dispositivos.',
            upgradeCta: 'Atualizar Agora',
            resendTitle: 'Reenviar passe QR',
            resendBtnTitle: 'Reenviar passe QR',
            resendFree: 'Grátis — não usa créditos de mensagens.',
            resendEmailBtn: 'Enviar o passe QR',
            resendSendingTo: 'Enviando para:',
            resendNoEmail: 'Este convidado não tem e-mail registado — partilhe o link do passe.',
            resendAddEmailHint: 'Sem email registrado: adicione um aqui e enviaremos o passe e o guardaremos na ficha.',
            resendAddEmailPlaceholder: 'convidado@exemplo.com',
            resendAddEmailBtn: 'Salvar e enviar o passe',
            resendEmailSaved: 'Email salvo',
            resendEmailInvalid: 'Isso não parece um endereço de email.',
            resendWhatsAppBtn: 'Enviar por WhatsApp',
            resendCopyBtn: 'Copiar link do passe',
            resendCopied: 'Link copiado',
            resendSent: 'Passe enviado por e-mail',
            resendFailed: 'Não foi possível enviar o passe',
            resendWhatsAppMsg: 'Aqui está o seu passe QR de check-in para',
            resendAllBtn: 'Reenviar passes QR',
            resendAllConfirm: 'Enviar novamente por e-mail a cada convidado confirmado o seu passe QR pessoal? Convidados sem e-mail são ignorados. É grátis e não usa créditos de mensagens.',
            resendAllDone: 'Passes enviados',
            resendAllNoEmail: 'sem e-mail',
            resendAllNone: 'Nenhum convidado confirmado tem e-mail registado',
            privacyLabel: 'Modo de privacidade no check-in autónomo',
            privacyDesc: 'O QR mestre mostra uma caixa de pesquisa e oculta a lista de convidados.',
            privacySaved: 'Modo de privacidade atualizado',
            privacySaveFailed: 'Não foi possível guardar a definição',
            statCheckedIn: 'Registrados',
            statNotYet: 'Ainda não',
            statExpected: 'Esperados',
            statRate: 'Taxa',
            progressLabel: 'Progresso do check-in',
            progressOf: '{n} de {total}',
            statWalkIns: 'Sem reserva',
            walkInChip: 'Sem reserva',
            qaTitle: 'A distribuição automática por QR está ativa, mas não há mesas',
            qaBody: 'Os convidados que fizerem check-in não receberão mesa. Adicione mesas primeiro na aba Lugares, ou abra mesmo assim.',
            qaAdd: 'Adicionar mesas',
            qaOpen: 'Abrir mesmo assim',
            qaCancel: 'Cancelar',
            qaMute: 'Não avisar de novo neste evento',
            tableNone: 'Sem mesa',
            tableChange: 'Trocar mesa',
            tableMoveTo: 'Mover {name} para',
            tablePartyOf: '(grupo de {n})',
            tableFullMark: 'cheia',
            tableFullConfirm: '{table} tem {free} lugar(es) livre(s) e este grupo precisa de {n}. Sentá-los lá mesmo assim?',
            tableMoveFailed: 'Não foi possível trocar a mesa'
        },
        de: {
            failedToLoad: 'Check-In-Daten konnten nicht geladen werden',
            noGuestsMatch: 'Keine Gäste entsprechen Ihrer Suche',
            noAttendingGuests: 'Keine bestätigten Gäste gefunden',
            sortName: "Name A–Z", sortOut: "Noch nicht eingecheckt zuerst", sortIn: "Eingecheckt zuerst", sortRecent: "Neueste Ankünfte zuerst", sortLabel: "Gäste sortieren",
            guest: 'Gast',
            checkedIn: 'Eingecheckt',
            pending: 'Ausstehend',
            partialTitle: 'Nur ein Teil dieser Gruppe ist eingecheckt',
            masterQrTitle: 'Master-QR-Code',
            masterQrDesc: 'Drucken Sie diesen QR-Code und stellen Sie ihn am Eingang auf. Gäste scannen ihn, um ihren Namen und Tisch zu finden.',
            masterQrOpen: 'Gäste-Link öffnen',
            masterQrDownload: 'Bild herunterladen',
            undo: 'Rückgängig',
            undoConfirm: 'Check-in rückgängig machen für',
            undoFailed: 'Check-in konnte nicht rückgängig gemacht werden',
            upgradeTitle: 'QR-Check-in freischalten',
            upgradeDesc: 'Upgradet dieses Event, um Gäste am Eingang per QR-Code einzuchecken — Ankünfte werden live auf allen Geräten aktualisiert.',
            upgradeCta: 'Jetzt upgraden',
            resendTitle: 'QR-Pass erneut senden',
            resendBtnTitle: 'QR-Pass erneut senden',
            resendFree: 'Kostenlos — verbraucht keine Nachrichten-Credits.',
            resendEmailBtn: 'QR-Pass senden',
            resendSendingTo: 'Senden an:',
            resendNoEmail: 'Keine E-Mail-Adresse hinterlegt — teilen Sie stattdessen den Pass-Link.',
            resendAddEmailHint: 'Keine E-Mail hinterlegt — hier eintragen, wir senden den Pass und merken sie uns.',
            resendAddEmailPlaceholder: 'gast@beispiel.de',
            resendAddEmailBtn: 'Speichern und Pass senden',
            resendEmailSaved: 'E-Mail gespeichert',
            resendEmailInvalid: 'Das sieht nicht nach einer E-Mail-Adresse aus.',
            resendWhatsAppBtn: 'Per WhatsApp senden',
            resendCopyBtn: 'Pass-Link kopieren',
            resendCopied: 'Link kopiert',
            resendSent: 'Pass per E-Mail gesendet',
            resendFailed: 'Pass konnte nicht gesendet werden',
            resendWhatsAppMsg: 'Hier ist Ihr Check-in-QR-Pass für',
            resendAllBtn: 'QR-Pässe erneut senden',
            resendAllConfirm: 'Jedem bestätigten Gast seinen persönlichen QR-Pass erneut per E-Mail senden? Gäste ohne E-Mail-Adresse werden übersprungen. Kostenlos, ohne Nachrichten-Credits.',
            resendAllDone: 'Pässe gesendet',
            resendAllNoEmail: 'ohne E-Mail',
            resendAllNone: 'Kein bestätigter Gast hat eine E-Mail-Adresse hinterlegt',
            privacyLabel: 'Privatsphäre-Modus für den Selbst-Check-in',
            privacyDesc: 'Der Master-QR zeigt ein Suchfeld und blendet die Gästeliste aus.',
            privacySaved: 'Privatsphäre-Modus aktualisiert',
            privacySaveFailed: 'Einstellung konnte nicht gespeichert werden',
            statCheckedIn: 'Eingecheckt',
            statNotYet: 'Noch nicht',
            statExpected: 'Erwartet',
            statRate: 'Quote',
            progressLabel: 'Check-in-Fortschritt',
            progressOf: '{n} von {total}',
            statWalkIns: 'Spontan',
            walkInChip: 'Spontan',
            qaTitle: 'QR-Auto-Platzierung ist an, aber es gibt keine Tische',
            qaBody: 'Gäste, die einchecken, bekommen keinen Tisch. Legen Sie zuerst im Tab Sitzplan Tische an, oder öffnen Sie trotzdem.',
            qaAdd: 'Tische hinzufügen',
            qaOpen: 'Trotzdem öffnen',
            qaCancel: 'Abbrechen',
            qaMute: 'Für diese Veranstaltung nicht mehr warnen',
            tableNone: 'Kein Tisch',
            tableChange: 'Tisch ändern',
            tableMoveTo: '{name} verschieben nach',
            tablePartyOf: '(Gruppe von {n})',
            tableFullMark: 'voll',
            tableFullConfirm: '{table} hat {free} freie Plätze, diese Gruppe braucht {n}. Trotzdem dort platzieren?',
            tableMoveFailed: 'Tisch konnte nicht geändert werden'
        },
        he: {
            failedToLoad: 'נכשל טעינת נתוני צ׳ק-אין',
            noGuestsMatch: 'לא נמצאו אורחים מתאימים',
            noAttendingGuests: 'לא נמצאו אורחים מאשרים',
            sortName: "שם א–ת", sortOut: "עוד לא נרשמו קודם", sortIn: "נרשמו קודם", sortRecent: "הגעות אחרונות קודם", sortLabel: "מיון אורחים",
            guest: 'אורח',
            checkedIn: 'נרשם',
            pending: 'ממתין',
            partialTitle: 'רק חלק מהקבוצה הזו נרשם',
            masterQrTitle: 'קוד QR לכניסה',
            masterQrDesc: 'הדפיסו את קוד ה-QR והציגו אותו בכניסה. האורחים סורקים למציאת שמם ושולחנם.',
            masterQrOpen: 'פתח קישור כניסת אורחים',
            masterQrDownload: 'הורד תמונה',
            undo: 'ביטול',
            undoConfirm: 'לבטל את הצ׳ק-אין של',
            undoFailed: 'לא ניתן לבטל את הצ׳ק-אין',
            upgradeTitle: 'פתחו צ׳ק-אין עם QR',
            upgradeDesc: 'שדרגו את האירוע כדי לרשום אורחים בכניסה עם קודי QR — ההגעות מתעדכנות בזמן אמת בכל המכשירים.',
            upgradeCta: 'שדרג עכשיו',
            resendTitle: 'שליחה חוזרת של כרטיס QR',
            resendBtnTitle: 'שליחה חוזרת של כרטיס QR',
            resendFree: 'חינם — לא צורך קרדיטים להודעות.',
            resendEmailBtn: 'שליחת כרטיס QR',
            resendSendingTo: 'נשלח אל:',
            resendNoEmail: 'לאורח אין כתובת מייל — שתפו את קישור הכרטיס במקום.',
            resendAddEmailHint: 'אין אימייל רשום — הוסיפו כאן ונשלח את הכרטיס ונשמור אותו בכרטיס האורח.',
            resendAddEmailPlaceholder: 'guest@example.com',
            resendAddEmailBtn: 'שמירה ושליחת הכרטיס',
            resendEmailSaved: 'האימייל נשמר',
            resendEmailInvalid: 'זו לא נראית כתובת אימייל.',
            resendWhatsAppBtn: 'שליחה בוואטסאפ',
            resendCopyBtn: 'העתקת קישור הכרטיס',
            resendCopied: 'הקישור הועתק',
            resendSent: 'הכרטיס נשלח במייל',
            resendFailed: 'לא ניתן לשלוח את הכרטיס',
            resendWhatsAppMsg: 'הנה כרטיס ה-QR שלך לצ׳ק-אין עבור',
            resendAllBtn: 'שליחה חוזרת של כרטיסי QR',
            resendAllConfirm: 'לשלוח שוב במייל לכל אורח מאשר את כרטיס ה-QR האישי שלו? אורחים ללא מייל ידולגו. זה חינם ולא צורך קרדיטים להודעות.',
            resendAllDone: 'כרטיסים נשלחו',
            resendAllNoEmail: 'ללא מייל',
            resendAllNone: 'לאף אורח מאשר אין כתובת מייל במערכת',
            privacyLabel: 'מצב פרטיות לצ׳ק-אין עצמאי',
            privacyDesc: 'ה-QR הראשי מציג תיבת חיפוש ומסתיר את רשימת האורחים.',
            privacySaved: 'מצב הפרטיות עודכן',
            privacySaveFailed: 'לא ניתן לשמור את ההגדרה',
            statCheckedIn: 'נרשמו',
            statNotYet: 'עוד לא',
            statExpected: 'צפויים',
            statRate: 'אחוז',
            progressLabel: 'התקדמות הצ׳ק-אין',
            progressOf: '{n} מתוך {total}',
            statWalkIns: 'ללא הרשמה',
            walkInChip: 'ללא הרשמה',
            qaTitle: 'הושבת QR אוטומטית פעילה, אבל אין שולחנות',
            qaBody: 'אורחים שיעשו צ׳ק-אין לא יקבלו שולחן. הוסיפו קודם שולחנות בלשונית ההושבה, או פתחו בכל זאת.',
            qaAdd: 'הוספת שולחנות',
            qaOpen: 'לפתוח בכל זאת',
            qaCancel: 'ביטול',
            qaMute: 'לא להזהיר שוב באירוע הזה',
            tableNone: 'ללא שולחן',
            tableChange: 'שינוי שולחן',
            tableMoveTo: 'העברת {name} אל',
            tablePartyOf: '(קבוצה של {n})',
            tableFullMark: 'מלא',
            tableFullConfirm: 'ב{table} יש {free} מקומות פנויים והקבוצה צריכה {n}. להושיב שם בכל זאת?',
            tableMoveFailed: 'לא ניתן לשנות את השולחן'
        },
        ar: {
            failedToLoad: 'فشل تحميل بيانات تسجيل الحضور',
            noGuestsMatch: 'لا يوجد ضيوف يطابقون بحثك',
            noAttendingGuests: 'لم يتم العثور على ضيوف مؤكدين',
            sortName: "الاسم أ–ي", sortOut: "غير المسجلين أولاً", sortIn: "المسجلون أولاً", sortRecent: "أحدث الوافدين أولاً", sortLabel: "ترتيب الضيوف",
            guest: 'ضيف',
            checkedIn: 'تم التسجيل',
            pending: 'قيد الانتظار',
            partialTitle: 'سجّل جزء فقط من هذه المجموعة الحضور',
            masterQrTitle: 'رمز QR للدخول',
            masterQrDesc: 'اطبع رمز QR وضعه عند المدخل. يقوم الضيوف بمسحه للعثور على اسمهم وطاولتهم.',
            masterQrOpen: 'فتح رابط تسجيل الضيوف',
            masterQrDownload: 'تحميل الصورة',
            undo: 'تراجع',
            undoConfirm: 'إلغاء تسجيل حضور',
            undoFailed: 'تعذّر إلغاء تسجيل الحضور',
            upgradeTitle: 'افتح تسجيل الدخول عبر QR',
            upgradeDesc: 'قم بترقية هذه المناسبة لتسجيل وصول الضيوف عند الباب عبر رموز QR — تُحدَّث الوصولات مباشرة على جميع الأجهزة.',
            upgradeCta: 'ترقية الآن',
            resendTitle: 'إعادة إرسال بطاقة QR',
            resendBtnTitle: 'إعادة إرسال بطاقة QR',
            resendFree: 'مجاني — لا يستهلك أرصدة الرسائل.',
            resendEmailBtn: 'إرسال بطاقة QR',
            resendSendingTo: 'الإرسال إلى:',
            resendNoEmail: 'لا يوجد بريد إلكتروني لهذا الضيف — شارك رابط البطاقة بدلاً من ذلك.',
            resendAddEmailHint: 'لا يوجد بريد مسجَّل — أضِف واحدًا هنا وسنرسل البطاقة ونحتفظ به في سجل الضيف.',
            resendAddEmailPlaceholder: 'guest@example.com',
            resendAddEmailBtn: 'حفظ وإرسال البطاقة',
            resendEmailSaved: 'تم حفظ البريد',
            resendEmailInvalid: 'لا يبدو هذا عنوان بريد إلكتروني.',
            resendWhatsAppBtn: 'إرسال عبر واتساب',
            resendCopyBtn: 'نسخ رابط البطاقة',
            resendCopied: 'تم نسخ الرابط',
            resendSent: 'أُرسلت البطاقة بالبريد الإلكتروني',
            resendFailed: 'تعذّر إرسال البطاقة',
            resendWhatsAppMsg: 'إليك بطاقة QR لتسجيل حضورك في',
            resendAllBtn: 'إعادة إرسال بطاقات QR',
            resendAllConfirm: 'إرسال بطاقة QR الشخصية بالبريد الإلكتروني مجددًا لكل ضيف مؤكد؟ سيتم تخطي الضيوف بدون بريد إلكتروني. هذا مجاني ولا يستهلك أرصدة الرسائل.',
            resendAllDone: 'بطاقات أُرسلت',
            resendAllNoEmail: 'بدون بريد إلكتروني',
            resendAllNone: 'لا يوجد ضيف مؤكد لديه بريد إلكتروني مسجل',
            privacyLabel: 'وضع الخصوصية لتسجيل الدخول الذاتي',
            privacyDesc: 'يعرض رمز QR الرئيسي مربع بحث ويخفي قائمة المدعوين.',
            privacySaved: 'تم تحديث وضع الخصوصية',
            privacySaveFailed: 'تعذّر حفظ الإعداد',
            statCheckedIn: 'تم تسجيلهم',
            statNotYet: 'لم يصلوا بعد',
            statExpected: 'المتوقعون',
            statRate: 'النسبة',
            progressLabel: 'تقدم تسجيل الوصول',
            progressOf: '{n} من {total}',
            statWalkIns: 'بدون تسجيل',
            walkInChip: 'بدون تسجيل',
            qaTitle: 'الجلوس التلقائي عبر QR مفعّل، لكن لا توجد طاولات',
            qaBody: 'الضيوف الذين يسجّلون حضورهم لن يحصلوا على طاولة. أضف طاولات في تبويب الجلوس أولاً، أو افتح على أي حال.',
            qaAdd: 'إضافة طاولات',
            qaOpen: 'فتح على أي حال',
            qaCancel: 'إلغاء',
            qaMute: 'لا تحذرني مجددًا لهذا الحدث',
            tableNone: 'بدون طاولة',
            tableChange: 'تغيير الطاولة',
            tableMoveTo: 'نقل {name} إلى',
            tablePartyOf: '(مجموعة من {n})',
            tableFullMark: 'ممتلئة',
            tableFullConfirm: 'في {table} {free} مقاعد شاغرة وهذه المجموعة تحتاج {n}. هل تريد إجلاسهم هناك على أي حال؟',
            tableMoveFailed: 'تعذّر تغيير الطاولة'
        }
    };

    constructor() {
        this.formId = null;
        this.unsubscribe = null;
        this.allResponses = [];
        this.searchFilter = '';
        // Task #894: 'all' | 'in' | 'out' (not yet checked in).
        this.statusFilter = 'all';
        this.isInitialized = false;
        this.lang = window.DASHBOARD_LANGUAGE || 'en';
        this.currentFormData = null;
        // Defaults to unlocked: if form data never arrives we fall back to the
        // old behaviour (load) rather than wrongly showing an upgrade wall.
        this.locked = false;
    }

    t(key) {
        const strings = DashboardCheckin.TRANSLATIONS[this.lang] || DashboardCheckin.TRANSLATIONS['en'];
        return strings[key] || DashboardCheckin.TRANSLATIONS['en'][key] || key;
    }

    isEligible(formData) {
        if (window.FeatureGates?.isPaidForm) {
            return window.FeatureGates.isPaidForm(formData);
        }
        // Fallback when FeatureGates hasn't loaded — accept any paid signal,
        // not just basic.active===true (which missed forms paid via legacy
        // flows that didn't set `active`).
        if (!formData) return false;
        const ps = formData.paymentStatus || {};
        return !!(
            ps.basic?.paid || ps.basic?.active ||
            ps.basic_signature?.paid || ps.basic_signature?.active ||
            ps.premium?.paid || ps.premium?.active ||
            formData.planType === 'basic' ||
            formData.planType === 'basic_signature' ||
            formData.planType === 'premium' ||
            formData.organizationId
        );
    }

    updateTabVisibility(formData) {
        this.currentFormData = formData;
        // Paid features keep their tab VISIBLE on every form — you can't upgrade
        // for something you can't see. Free forms get an upgrade prompt when they
        // open it instead (same model as the seating tab). Demo shows the real thing.
        this.locked = !window.isDemoMode && !this.isEligible(formData);
        const tabNav = document.getElementById('checkin-tab-nav');
        if (tabNav) tabNav.style.display = '';
        // If the tab is already open when the form data lands, re-gate it.
        if (this.locked && document.getElementById('checkin-tab')?.classList.contains('active')) {
            this.showUpgradeState();
        }
    }

    async init(formId) {
        if (!formId) {
            this.showNoFormError();
            return;
        }

        // Clean up previous listener
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
        if (this._unsubSeating) { try { this._unsubSeating(); } catch (e) { /* gone */ } this._unsubSeating = null; }
        this.seatTables = [];
        this._closeTableMenu();

        // Free form → upgrade prompt instead of the check-in UI. Bail BEFORE the
        // Firestore listener so a locked tab costs zero reads.
        if (this.locked) {
            this.showUpgradeState();
            return;
        }

        this.formId = formId;
        this.showLoading();

        try {
            // Set up real-time listener using Firebase client SDK. Bound it
            // with limit() so a check-in tab left open on a huge form doesn't
            // re-pay the full responses sub-collection on each snapshot. 1000
            // covers all realistic event sizes; if you have a >1000-guest
            // event, raise this and add pagination.
            const db = firebase.firestore();
            const CHECKIN_LISTENER_LIMIT = 1000;
            // The replies this plan lets the host see (Event Pro shows 150): the
            // listener reads every row, so the cap is applied to each snapshot,
            // exactly as on the Responses tab.
            const rlm = window.ResponseLimitManager;
            const cap = rlm ? await rlm.capFor(formId) : Infinity;
            // orderBy `timestamp` (not `submittedAt`) — legacy responses written
            // before `submittedAt` was added only have `timestamp`, and Firestore's
            // orderBy silently skips docs missing the indexed field. Using
            // `timestamp` matches getFormResponses() and keeps the full list intact.
            this.unsubscribe = db.collection('rsvp_forms').doc(formId)
                .collection('responses')
                .orderBy('timestamp', 'desc')
                .limit(CHECKIN_LISTENER_LIMIT)
                .onSnapshot((snapshot) => {
                    this.allResponses = [];
                    const rows = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    const visible = rlm ? rlm.constructor.applyCap(rows, cap).visible : rows;
                    visible.forEach(data => {
                        if (data.attending !== false && data.attending !== 'no') {
                            // Check-in reads Firestore directly rather than the
                            // dashboard's shared list, so it needs the same demo
                            // content localization or the marketing demo would
                            // show Hebrew names on Responses and English ones
                            // here (task #391). No-op outside demo mode.
                            const row = data;
                            this.allResponses.push(
                                typeof localizeDemoResponse === 'function' ? localizeDemoResponse(row) : row
                            );
                        }
                    });
                    this.render();
                }, (error) => {
                    console.error('Check-in snapshot error:', error);
                    this.showError(this.t('failedToLoad'));
                });
            this._watchSeating(db, formId);

            // Detach on page unload as a safety net.
            if (!this._checkinTeardownBound) {
                this._checkinTeardownBound = true;
                window.addEventListener('pagehide', () => {
                    if (this.unsubscribe) {
                        try { this.unsubscribe(); } catch (e) { /* ignore */ }
                        this.unsubscribe = null;
                    }
                });
            }

            // Set scanner link. The host camera scanner relies on per-guest
            // QR codes (a Signature-tier feature), so it's hidden for Lite
            // Event Pro forms — they only get the self-serve master QR.
            // Task #1123: it follows the tier alone, not the QR check-in
            // switch: a reminder can attach personal codes with that switch
            // off, and the scan checks only the code's own signature.
            const scannerBtn = document.getElementById('checkinOpenScannerBtn');
            if (scannerBtn) {
                const formDocRef = await db.collection('rsvp_forms').doc(formId).get();
                const formData = formDocRef.exists ? formDocRef.data() : null;
                const ps = formData?.paymentStatus || {};
                const hasIndividualQr = !!(
                    ps.basic_signature?.paid || ps.basic_signature?.active ||
                    ps.premium?.paid || ps.premium?.active ||
                    formData?.planType === 'basic_signature' ||
                    formData?.planType === 'premium' ||
                    formData?.organizationId
                );

                if (formData && hasIndividualQr) {
                    scannerBtn.style.display = 'inline-block';
                    scannerBtn.href = `/checkin?formId=${formId}`;
                } else {
                    scannerBtn.style.display = 'none';
                }

                // Task #419 — resend controls exist only where personal passes
                // exist, so they follow the scanner button's eligibility.
                this.canResendPasses = !!(formData && formData.enableCheckin && hasIndividualQr);
                let resendAllBtn = document.getElementById('checkinResendAllBtn');
                if (!resendAllBtn && this.canResendPasses) {
                    resendAllBtn = document.createElement('button');
                    resendAllBtn.type = 'button';
                    resendAllBtn.id = 'checkinResendAllBtn';
                    resendAllBtn.className = 'btn btn-outline-secondary';
                    resendAllBtn.innerHTML = `<i class="bi bi-send me-1"></i>${this.t('resendAllBtn')}`;
                    scannerBtn.insertAdjacentElement('afterend', resendAllBtn);
                    resendAllBtn.addEventListener('click', () => this.resendAllPasses(resendAllBtn));
                }
                if (resendAllBtn) resendAllBtn.style.display = this.canResendPasses ? 'inline-block' : 'none';

                // The first snapshot may have rendered before this flag was
                // known — re-render so eligible rows get their resend button.
                if (this.canResendPasses && this.allResponses.length) this.renderTable();

                this.renderPrivacyToggle(formData);
            }

            this.setupEventListeners();
            this.isInitialized = true;
        } catch (error) {
            console.error('Error initializing check-in tab:', error);
            this.showError('Failed to load check-in data');
        }
    }

    showLoading() {
        const loading = document.getElementById('checkinLoadingState');
        const content = document.getElementById('checkinMainContent');
        const noForm = document.getElementById('checkinNoFormError');
        if (loading) loading.style.display = '';
        if (content) content.style.display = 'none';
        if (noForm) noForm.style.display = 'none';
        this.hideUpgradeState();
    }

    showNoFormError() {
        const loading = document.getElementById('checkinLoadingState');
        const content = document.getElementById('checkinMainContent');
        const noForm = document.getElementById('checkinNoFormError');
        if (loading) loading.style.display = 'none';
        if (content) content.style.display = 'none';
        if (noForm) noForm.style.display = '';
        this.hideUpgradeState();
    }

    hideUpgradeState() {
        const up = document.getElementById('checkinUpgradeState');
        if (up) up.style.display = 'none';
    }

    // Locked (free) form: show why check-in is worth having + a way to unlock it.
    // Visual language matches the seating tab's upgrade overlay.
    showUpgradeState() {
        const loading = document.getElementById('checkinLoadingState');
        const content = document.getElementById('checkinMainContent');
        const noForm = document.getElementById('checkinNoFormError');
        const up = document.getElementById('checkinUpgradeState');
        if (loading) loading.style.display = 'none';
        if (content) content.style.display = 'none';
        if (noForm) noForm.style.display = 'none';
        if (!up) return;
        up.style.display = '';
        up.innerHTML = `
            <div style="display:flex;align-items:center;justify-content:center;padding:80px 20px 40px;border-radius:8px;">
                <div class="text-center" style="max-width:380px;">
                    <div style="width:64px;height:64px;margin:0 auto 16px;border-radius:50%;background:linear-gradient(135deg,#16243F,#a18bc4);display:flex;align-items:center;justify-content:center;">
                        <i class="bi bi-qr-code-scan text-white" style="font-size:1.8rem;"></i>
                    </div>
                    <h5 class="fw-bold" style="color:#3a3150;">${this.t('upgradeTitle')}</h5>
                    <p class="text-muted small mb-3">${this.t('upgradeDesc')}</p>
                    <button onclick="triggerHeaderUpgrade()" class="btn btn-primary px-4" style="background:linear-gradient(135deg,#16243F,#a18bc4);border:none;">
                        <i class="bi bi-arrow-up-circle me-1"></i>${this.t('upgradeCta')}
                    </button>
                </div>
            </div>`;
    }

    showError(msg) {
        const loading = document.getElementById('checkinLoadingState');
        const content = document.getElementById('checkinMainContent');
        this.hideUpgradeState();
        if (loading) loading.style.display = 'none';
        if (content) {
            content.style.display = '';
            content.innerHTML = `<div class="text-center py-4 text-danger">
                <i class="bi bi-exclamation-triangle" style="font-size: 2rem;"></i>
                <p class="mt-2">${msg}</p>
            </div>`;
        }
    }

    /**
     * Task #829: the Check-in tab laid out like the business (organization)
     * one - a status bar of counters across the top, a slim progress line
     * under it, then one toolbar with the search on the left and the door
     * tools grouped on the right. Built here rather than in dashboard.html so
     * the seven language copies of that page do not each need the same edit;
     * the buttons are MOVED, not recreated, so their ids, links and handlers
     * stay exactly as they were. Runs once.
     */
    _ensureLayout() {
        const main = document.getElementById('checkinMainContent');
        if (!main || main.dataset.ciLayout === '1') return;
        main.dataset.ciLayout = '1';
        main.classList.add('ci-shell');
        DashboardCheckin._injectStyles();

        // The row holding the door buttons. Found from a button, not from the
        // .checkin-primary-actions wrapper: the six language copies of the
        // page never got that wrapper, and their progress bar sits loose in
        // the row rather than inside .checkin-counts.
        const row = document.getElementById('checkinMasterQrBtn')?.closest('#checkinMainContent > div');
        main.querySelector('.checkin-counts')?.remove();
        document.getElementById('checkinProgressBar')?.closest('.progress')?.remove();

        const stats = document.createElement('div');
        stats.className = 'ci-stats';
        stats.id = 'checkinStatsBar';
        // Task #894 round 3: the tiles ARE the filter. Pressing one shows only
        // those guests and pressing it again shows everyone; the picker that
        // used to sit beside the search is gone, so search, sort and the door
        // buttons share one line. Rate is a ratio, not a group of guests.
        stats.innerHTML = [
            ['ci-in', 'checkinStatIn', 'statCheckedIn', 'in'],
            ['', 'checkinStatNotYet', 'statNotYet', 'out'],
            ['ci-expected', 'checkinStatExpected', 'statExpected', 'expected'],
            ['ci-walkin', 'checkinStatWalkIns', 'statWalkIns', 'walkin'],
            ['', 'checkinStatRate', 'statRate', '']
        ].map(([cls, id, key, filter]) => filter
            ? `<button type="button" class="ci-stat ${cls}" data-ci-filter="${filter}" aria-pressed="false"><span class="ci-stat-value" id="${id}">0</span><span class="ci-stat-label">${this.t(key)}</span></button>`
            : `<div class="ci-stat ${cls}"><span class="ci-stat-value" id="${id}">0</span><span class="ci-stat-label">${this.t(key)}</span></div>`
        ).join('');
        stats.addEventListener('click', (e) => {
            const tile = e.target.closest('[data-ci-filter]');
            if (!tile) return;
            const f = tile.dataset.ciFilter;
            this.statusFilter = this._statusFilter() === f ? 'all' : f;
            this.renderTable();
        });
        const progress = document.createElement('div');
        progress.className = 'ci-progress';
        progress.innerHTML = `
            <div class="ci-progress-head"><span>${this.t('progressLabel')}</span><span class="ci-progress-meta" id="checkinProgressMeta"></span></div>
            <div class="ci-progress-track"><div class="ci-progress-fill" id="checkinProgressFill" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" aria-label="${this.escapeHtml(this.t('progressLabel'))}"></div></div>`;
        main.insertBefore(stats, main.firstElementChild);
        stats.insertAdjacentElement('afterend', progress);

        if (row) {
            row.className = 'ci-toolbar';
            const left = document.createElement('div');
            left.className = 'ci-toolbar-left';
            const search = main.querySelector('.seating-search-wrap');
            const searchBlock = search && search.parentElement;
            if (search) {
                search.style.marginInlineStart = '0';
                left.appendChild(search);
            }
            if (searchBlock && searchBlock !== row && !searchBlock.children.length) searchBlock.remove();
            const right = document.createElement('div');
            right.className = 'ci-toolbar-right';
            // Everything that was in the row (the two primary buttons, the
            // scanner, later the resend-all button) becomes the right group.
            [...row.children].forEach((el) => right.appendChild(el));
            row.appendChild(left);
            row.appendChild(right);
            progress.insertAdjacentElement('afterend', row);
        }
    }

    static _injectStyles() {
        if (document.getElementById('ci-layout-styles')) return;
        const s = document.createElement('style');
        s.id = 'ci-layout-styles';
        s.textContent = [
            // #829 round 2: one raised surface for the whole tab, like the
            // dashboard's other cards, so nothing runs into the page edge.
            '#checkinMainContent.ci-shell{background:#fff;border:1px solid #eef0f3;border-radius:12px;box-shadow:0 4px 14px rgba(0,0,0,.07);padding:1.25rem;}',
            // Counters as separate floating tiles, value over label.
            '.ci-stats{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:.75rem;margin-bottom:.75rem;}',
            '.ci-stat{display:flex;flex-direction:column;gap:.2rem;padding:.7rem .9rem;background:#fff;border:1px solid #eef0f3;border-radius:10px;box-shadow:0 1px 3px rgba(16,24,40,.06);min-width:0;}',
            '.ci-stat-value{font-size:1.3rem;font-weight:700;color:#1f2937;line-height:1.1;}',
            '.ci-stat-label{font-size:.7rem;text-transform:uppercase;letter-spacing:.06em;color:#6b7280;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}',
            '.ci-stat.ci-in .ci-stat-value{color:#198754;}',
            '.ci-stat.ci-expected .ci-stat-value{color:#4c3a8c;}',
            '.ci-stat.ci-walkin .ci-stat-value{color:#6d28d9;}',
            // Pressable tiles (#894 round 3): a button that still looks like the tile.
            'button.ci-stat{font:inherit;color:inherit;text-align:start;cursor:pointer;transition:border-color .15s,background-color .15s;}',
            'button.ci-stat:hover{border-color:#c9c1e6;}',
            'button.ci-stat:focus-visible{outline:2px solid #4c3a8c;outline-offset:2px;}',
            'button.ci-stat.is-active{border-color:#4c3a8c;background:#f6f5fb;box-shadow:inset 0 0 0 1px #4c3a8c;}',
            '.ci-walkin-chip{display:inline-block;margin-inline-start:.4rem;padding:.05rem .45rem;border-radius:999px;background:#f1edfb;color:#4c3a8c;font-size:.7rem;font-weight:600;vertical-align:middle;}',
            '.ci-progress{margin-bottom:1.1rem;padding:.7rem .9rem;background:#fff;border:1px solid #eef0f3;border-radius:10px;box-shadow:0 1px 3px rgba(16,24,40,.06);}',
            '.ci-progress-head{display:flex;justify-content:space-between;font-size:.78rem;color:#6b7280;margin-bottom:.4rem;}',
            '.ci-progress-meta{color:#1f2937;font-weight:600;}',
            '.ci-progress-track{height:6px;background:#f1f3f5;border-radius:999px;overflow:hidden;}',
            '.ci-progress-fill{height:100%;width:0;background:linear-gradient(90deg,#20a464,#198754);border-radius:999px;transition:width .3s ease;}',
            // Search left, door tools right, privacy switch on its own line
            // under the search.
            '.ci-toolbar{display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:.6rem .75rem;margin-bottom:1rem;}',
            // Search, then the status filter and sort pickers (#894) on one line.
            '.ci-toolbar-left{flex:1 1 220px;min-width:0;display:flex;flex-wrap:wrap;align-items:center;gap:.5rem;}',
            '.ci-toolbar-left .seating-search-wrap{flex:1 1 200px;max-width:320px !important;margin:0 !important;}',
            '.ci-toolbar-right{display:flex;flex-wrap:wrap;gap:.5rem;align-items:center;}',
            // flex-direction: admin.css stacks every .d-flex.gap-2 on phones,
            // which undid task #398's side-by-side pair.
            '#checkinMainContent .ci-toolbar-right .checkin-primary-actions{display:flex;flex-direction:row;gap:.5rem;}',
            '.ci-toolbar #checkinPrivacyToggleWrap{flex-basis:100%;margin:0 !important;font-size:.85rem;}',
            // Door tools at the business view's button size and weight.
            '#checkinMainContent .ci-toolbar-right .btn{padding:.42rem .8rem;font-size:.8rem;font-weight:500;line-height:1.2;border-radius:8px;white-space:nowrap;box-shadow:none;}',
            // !important: admin.css forces .btn-outline-secondary grey with !important.
            '#checkinMainContent .ci-toolbar-right .btn:not(.btn-primary){background:#fff !important;color:#1f2937 !important;border:1px solid #d1d5db !important;}',
            '#checkinMainContent .ci-toolbar-right .btn:not(.btn-primary):hover{background:#f6f5fb !important;border-color:#4c3a8c !important;color:#4c3a8c !important;}',
            '@media (max-width:720px){#checkinMainContent.ci-shell{padding:.9rem;}.ci-stats{grid-template-columns:repeat(2,minmax(0,1fr));gap:.5rem;}.ci-stats .ci-stat:last-child{grid-column:1 / -1;}.ci-toolbar-right{width:100%;}.ci-toolbar-left .seating-search-wrap{flex-basis:100%;max-width:none !important;}.ci-toolbar-left > .form-select{flex:1 1 0 !important;min-width:0;}#checkinMainContent .ci-toolbar-right .checkin-primary-actions{width:100%;}#checkinMainContent .ci-toolbar-right .btn{display:inline-flex;align-items:center;justify-content:center;flex:1 1 calc(50% - .25rem);min-width:0;white-space:normal;text-align:center;}#checkinMainContent .table-responsive{margin-inline:0 !important;}}'
        ].join('\n');
        document.head.appendChild(s);
    }

    render() {
        document.getElementById('checkinLoadingState').style.display = 'none';
        document.getElementById('checkinMainContent').style.display = '';
        this._ensureLayout();

        // Walk-ins (task #852) were added at the door, so they were never
        // expected: Expected, Not yet, Rate and the progress line describe the
        // guests who registered, Walk-ins counts the rest, and Checked in is
        // everyone who is here.
        const registered = this.allResponses.filter(r => r.walkIn !== true);
        const total = registered.length;
        const registeredIn = registered.filter(r => r.checkedInAt).length;
        const checkedIn = this.allResponses.filter(r => r.checkedInAt).length;
        const walkIns = this.allResponses.length - registered.length;
        const pct = total > 0 ? Math.round((registeredIn / total) * 100) : 0;

        // Update the status bar (task #829)
        const set = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
        set('checkinStatIn', checkedIn);
        set('checkinStatNotYet', total - registeredIn);
        set('checkinStatExpected', total);
        set('checkinStatWalkIns', walkIns);
        set('checkinStatRate', pct + '%');
        set('checkinProgressMeta', this.t('progressOf').replace('{n}', registeredIn).replace('{total}', total));
        const fill = document.getElementById('checkinProgressFill');
        if (fill) {
            fill.style.width = pct + '%';
            fill.setAttribute('aria-valuenow', String(pct));
        }

        // Always-visible "Open Guest Check-In Link" button. Same page the
        // Master QR encodes, but with the door key once it has loaded (task
        // #852), so whoever runs the door from it can also add walk-ins.
        this._applyGuestLink();
        this._loadDoorKey();

        this.renderTable();
    }

    _applyGuestLink() {
        const guestLinkBtn = document.getElementById('checkinGuestLinkBtn');
        if (!guestLinkBtn || !this.formId) return;
        const base = `${window.location.origin}/guest-lookup.html?formId=${this.formId}`;
        const key = this._doorKey && this._doorKey.formId === this.formId ? this._doorKey.key : null;
        guestLinkBtn.href = key ? `${base}&door=${encodeURIComponent(key)}` : base;
    }

    // Host-only key, fetched once per event; the demo has no signed-in host.
    async _loadDoorKey() {
        const formId = this.formId;
        if (!formId || window.isDemoMode) return;
        if (this._doorKey && this._doorKey.formId === formId) return;
        this._doorKey = { formId, key: null };
        try {
            const token = await firebase.auth().currentUser?.getIdToken();
            if (!token) return;
            const r = await fetch(`/api/checkin/${encodeURIComponent(formId)}/door-link`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await r.json().catch(() => ({}));
            if (r.ok && data.doorKey && this._doorKey.formId === formId) {
                this._doorKey.key = data.doorKey;
                this._applyGuestLink();
            }
        } catch (err) {
            console.warn('Door-staff link unavailable:', err.message);
        }
    }

    // Does this response match the search query by ANY of its names — the main
    // guest or any additional party member (plus-ones)? Mirrors the seating
    // search so a +1 is findable everywhere, not only in the seating chart.
    _matchesGuestSearch(r, q) {
        if (!q) return true;
        const names = [r.guestName, r.name];
        if (Array.isArray(r.individualNames)) {
            r.individualNames.forEach(e => names.push((e && typeof e === 'object') ? e.name : e));
        }
        const extra = Array.isArray(r.guests) ? r.guests
            : Array.isArray(r.additionalGuests) ? r.additionalGuests : [];
        extra.forEach(g => names.push(typeof g === 'string' ? g : (g && (g.guestName || g.name || g.fullName || g.guest_name))));
        // Task #965 round 3: the door list names "Esti" on the Smiths'
        // invitation Esti Smiths, so the hostess may type that, or Esti Smith,
        // or the family name itself.
        const H = window.HouseholdNames;
        const mainName = r.guestName || r.name || '';
        if (r.inviteGroupName) names.push(r.inviteGroupName);
        if (H && H.partySearchNames) {
            names.slice().forEach(n => { if (n) names.push(...H.partySearchNames(String(n), mainName, r.inviteGroupName)); });
        }
        return names.some(n => n && String(n).toLowerCase().includes(q));
    }

    /**
     * Which group the stat tiles are showing (task #894): 'all', or the
     * tile pressed - 'in' (checked in), 'out' (registered, not here yet),
     * 'expected' (everyone registered) or 'walkin' (added at the door). Each
     * matches what its tile counts.
     */
    _statusFilter() {
        return ['in', 'out', 'expected', 'walkin'].includes(this.statusFilter) ? this.statusFilter : 'all';
    }

    _matchesStatus(r, f) {
        if (f === 'in') return !!r.checkedInAt;
        if (f === 'out') return !r.checkedInAt && r.walkIn !== true;
        if (f === 'expected') return r.walkIn !== true;
        if (f === 'walkin') return r.walkIn === true;
        return true;
    }

    _renderStatusFilter() {
        const f = this._statusFilter();
        document.querySelectorAll('#checkinStatsBar [data-ci-filter]').forEach((tile) => {
            const on = tile.dataset.ciFilter === f;
            tile.classList.toggle('is-active', on);
            tile.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        const search = document.getElementById('checkinGuestSearch');
        if (!search) return;

        // Sort by check-in status, right after the search. Every order ends in A-Z.
        let sort = document.getElementById('checkinSort');
        if (!sort) {
            sort = document.createElement('select');
            sort.id = 'checkinSort';
            sort.className = 'form-select form-select-sm';
            sort.style.cssText = 'width:auto;flex:0 0 auto;';
            sort.setAttribute('aria-label', this.t('sortLabel'));
            ['name', 'out', 'in', 'recent'].forEach((v) => { const o = document.createElement('option'); o.value = v; sort.appendChild(o); });
            sort.addEventListener('change', (e) => { this.sortOrder = e.target.value; this.renderTable(); });
            (search.closest('.seating-search-wrap') || search).insertAdjacentElement('afterend', sort);
        }
        sort.value = this._sortOrder();
        const words = { name: 'sortName', out: 'sortOut', in: 'sortIn', recent: 'sortRecent' };
        [...sort.options].forEach((o) => { o.textContent = this.t(words[o.value]); });
    }

    _sortOrder() {
        return ['out', 'in', 'recent'].includes(this.sortOrder) ? this.sortOrder : 'name';
    }

    _compareGuests(a, b) {
        const order = this._sortOrder();
        if (order !== 'name' && !!a.checkedInAt !== !!b.checkedInAt) {
            return (order === 'out') === !a.checkedInAt ? -1 : 1;
        }
        if (order === 'recent' && a.checkedInAt && b.checkedInAt) {
            const at = (r) => { const d = r.checkedInAt.toDate ? r.checkedInAt.toDate() : new Date(r.checkedInAt); return isNaN(d) ? 0 : d.getTime(); };
            if (at(a) !== at(b)) return at(b) - at(a);
        }
        return String(a.guestName || a.name || '').localeCompare(String(b.guestName || b.name || ''), undefined, { sensitivity: 'base' });
    }

    renderTable() {
        const tbody = document.getElementById('checkinGuestTableBody');
        if (!tbody) return;

        let responses = [...this.allResponses];

        // Apply search filter — match the main guest OR any additional party
        // member (plus-ones), so searching a +1's name surfaces their reservation.
        if (this.searchFilter) {
            const q = this.searchFilter.trim().toLowerCase();
            responses = responses.filter(r => this._matchesGuestSearch(r, q));
        }

        // Task #894: All / Checked in / Not yet, then the chosen order - A-Z
        // by default, so rows don't move on every scan.
        this._renderStatusFilter();
        const status = this._statusFilter();
        if (status !== 'all') {
            responses = responses.filter(r => this._matchesStatus(r, status));
        }
        responses.sort((a, b) => this._compareGuests(a, b));

        if (responses.length === 0) {
            tbody.innerHTML = `<tr><td colspan="4" class="text-center text-muted py-3">
                ${this.searchFilter || this._statusFilter() !== 'all' ? this.t('noGuestsMatch') : this.t('noAttendingGuests')}
            </td></tr>`;
            return;
        }

        tbody.innerHTML = responses.map(r => {
            const name = r.guestName || r.name || this.t('guest');
            // Shared resolver — the old expression added children on top of
            // guestCount, which already includes them, and ignored the legacy
            // adults pair entirely.
            const partySize = resolveGuestCounts(r).total;
            const isCheckedIn = !!r.checkedInAt;
            // Partial check-in: only some of a 2+ party actually arrived. The
            // server records checkedInCount + checkedInPartial on both the
            // personal-QR and Master-QR paths.
            const checkedInCount = (typeof r.checkedInCount === 'number') ? r.checkedInCount : partySize;
            const isPartial = isCheckedIn && partySize > 1
                && (r.checkedInPartial === true || checkedInCount < partySize)
                && checkedInCount > 0;
            let checkedInTime = '';

            if (isCheckedIn) {
                const dt = r.checkedInAt.toDate ? r.checkedInAt.toDate() : new Date(r.checkedInAt);
                checkedInTime = dt.toLocaleString(undefined, {
                    month: 'short', day: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                });
            }

            let statusBadge;
            if (!isCheckedIn) {
                statusBadge = `<span class="badge bg-warning text-dark">${this.t('pending')}</span>`;
            } else if (isPartial) {
                statusBadge = `<span class="badge text-white" style="background:#fd7e14;" title="${this.t('partialTitle')}"><i class="bi bi-people-fill me-1"></i>${checkedInCount}/${partySize} ${this.t('checkedIn')}</span>`;
            } else {
                statusBadge = `<span class="badge bg-success"><i class="bi bi-check-circle me-1"></i>${this.t('checkedIn')}</span>`;
            }

            // Undo control on checked-in rows — for guests checked in by mistake.
            const undoBtn = isCheckedIn
                ? `<button type="button" class="btn btn-sm btn-outline-danger checkin-undo-btn" data-response-id="${r.id}" data-guest-name="${this.escapeHtml(name)}" title="${this.t('undo')}"><i class="bi bi-arrow-counterclockwise"></i></button>`
                : '';

            // Resend-pass control on rows that haven't arrived yet (task #419) —
            // the "guest lost their ticket" case. Opens the free resend modal.
            // Only where personal passes exist (same gate as the scanner button).
            const resendBtn = (!isCheckedIn && this.canResendPasses)
                ? `<button type="button" class="btn btn-sm btn-outline-secondary checkin-resend-btn" data-response-id="${r.id}" data-guest-name="${this.escapeHtml(name)}" data-guest-email="${this.escapeHtml(r.email || r.guestEmail || '')}" title="${this.t('resendBtnTitle')}"><i class="bi bi-send"></i></button>`
                : '';

            // Timestamp + undo live in a space-between flex row so every undo
            // button lands in the same column position, however wide the date is.
            const checkedInCell = (checkedInTime || undoBtn || resendBtn)
                ? `<div class="d-flex align-items-center justify-content-between gap-2"><span>${checkedInTime}</span>${undoBtn}${resendBtn}</div>`
                : '';

            return `<tr>
                <td>${this.escapeHtml(name)}${r.walkIn === true ? ` <span class="ci-walkin-chip">${this.escapeHtml(this.t('walkInChip'))}</span>` : ''}${this._tableChipHtml(r)}</td>
                <td>${partySize}</td>
                <td>${statusBadge}</td>
                <td class="text-muted small text-nowrap">${checkedInCell}</td>
            </tr>`;
        }).join('');
    }

    // ---- The guest's table, and changing it here (task #871 r2) ----------
    // Each guest shows their table under the name; a click lists the tables
    // with how full each is, and a pick moves the whole party in one server
    // transaction (POST /api/seating/:id/move-party - the same move the door
    // uses), so it never overwrites a seat given at the door meanwhile. The
    // chart is followed live (one document), as the Seating tab follows it.
    _watchSeating(db, formId) {
        this.seatTables = [];
        if (window.isDemoMode || window.viewerMode) return;
        try {
            this._unsubSeating = db.collection('rsvp_forms').doc(formId)
                .collection('seating').doc('config')
                .onSnapshot((snap) => {
                    if (this.formId !== formId) return;
                    const tables = snap.exists && Array.isArray(snap.data().tables) ? snap.data().tables : [];
                    this.seatTables = tables;
                    this.seatMode = snap.exists ? (snap.data().assignmentMode || 'manual') : 'manual';
                    if (this._tableMenu) this._closeTableMenu();
                    this.renderTable();
                }, () => { this.seatTables = []; });
        } catch (e) { this.seatTables = []; }
    }

    _qaMuted() {
        try { return localStorage.getItem('qrAutoNoTablesDismissed_' + this.formId) === '1'; } catch (e) { return false; }
    }

    _guardDoorOpen(e, link) {
        if (this.seatMode !== 'qr_auto' || (this.seatTables && this.seatTables.length) || this._qaMuted()) return;
        e.preventDefault();
        const href = link.href;
        document.getElementById('ciQrAutoAsk')?.remove();
        document.body.insertAdjacentHTML('beforeend', `
          <div class="modal fade" id="ciQrAutoAsk" tabindex="-1" aria-labelledby="ciQrAutoAskTitle" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered"><div class="modal-content" dir="${document.documentElement.dir || 'ltr'}">
              <div class="modal-header border-0 pb-0">
                <h6 class="modal-title" id="ciQrAutoAskTitle"><i class="bi bi-exclamation-triangle-fill text-warning me-2"></i>${this.escapeHtml(this.t('qaTitle'))}</h6>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${this.escapeHtml(this.t('qaCancel'))}"></button>
              </div>
              <div class="modal-body pt-2">
                <p class="mb-2">${this.escapeHtml(this.t('qaBody'))}</p>
                <label class="form-check small text-muted mb-0"><input type="checkbox" class="form-check-input" id="ciQrAutoAskMute"> ${this.escapeHtml(this.t('qaMute'))}</label>
              </div>
              <div class="modal-footer border-0 pt-0">
                <button type="button" class="btn btn-outline-secondary btn-sm" data-bs-dismiss="modal">${this.escapeHtml(this.t('qaCancel'))}</button>
                <button type="button" class="btn btn-outline-primary btn-sm" data-qa-act="open">${this.escapeHtml(this.t('qaOpen'))}</button>
                <button type="button" class="btn btn-primary btn-sm" data-qa-act="tables"><i class="bi bi-grid-3x3 me-1"></i>${this.escapeHtml(this.t('qaAdd'))}</button>
              </div>
            </div></div>
          </div>`);
        const el = document.getElementById('ciQrAutoAsk');
        const modal = new bootstrap.Modal(el);
        el.addEventListener('click', (ev) => {
            const act = ev.target.closest('[data-qa-act]')?.dataset.qaAct;
            if (!act) return;
            if (el.querySelector('#ciQrAutoAskMute').checked) {
                try { localStorage.setItem('qrAutoNoTablesDismissed_' + this.formId, '1'); } catch (err) { /* private mode */ }
            }
            // Inside the click itself, or the browser blocks the new tab.
            if (act === 'open') window.open(href, '_blank', 'noopener');
            modal.hide();
            if (act === 'tables') document.getElementById('seating-tab')?.click();
        });
        el.addEventListener('hidden.bs.modal', () => el.remove(), { once: true });
        modal.show();
    }

    _tablesOf(responseId) {
        const names = [];
        (this.seatTables || []).forEach(t => {
            if ((t.seats || []).some(s => s && s.responseId === responseId)) names.push(String(t.name || '').trim() || 'Table');
        });
        return names;
    }

    _tableChipHtml(r) {
        if (!this.seatTables || !this.seatTables.length) return '';
        this._ensureTableStyles();
        const at = this._tablesOf(r.id);
        return `<div><button type="button" class="ci-table-chip${at.length ? '' : ' is-none'}" data-response-id="${this.escapeHtml(r.id)}" aria-haspopup="true" title="${this.escapeHtml(this.t('tableChange'))}">`
            + `<i class="bi bi-grid-3x3-gap"></i><span>${this.escapeHtml(at.length ? at.join(', ') : this.t('tableNone'))}</span><i class="bi bi-chevron-down"></i></button></div>`;
    }

    _ensureTableStyles() {
        if (document.getElementById('ciTableStyles')) return;
        const st = document.createElement('style');
        st.id = 'ciTableStyles';
        st.textContent = '.ci-table-chip{display:inline-flex;align-items:center;gap:5px;max-width:14rem;margin-top:4px;padding:2px 9px;border:1px solid #dee2e6;border-radius:999px;background:#fff;color:#334155;font-size:.78rem;font-weight:600;cursor:pointer;}'
            + '.ci-table-chip span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}'
            + '.ci-table-chip .bi-chevron-down{font-size:.6rem;color:#94a3b8;}'
            + '.ci-table-chip:hover,.ci-table-chip:focus-visible{border-color:#94a3b8;background:#f8fafc;outline:0;}'
            + '.ci-table-chip.is-none{color:#6b7280;font-weight:500;border-style:dashed;}'
            + '.ci-table-menu{position:fixed;z-index:1090;min-width:14rem;max-width:calc(100vw - 16px);max-height:min(22rem,70vh);overflow:auto;background:#fff;border:1px solid #e5e7eb;border-radius:10px;box-shadow:0 10px 28px rgba(0,0,0,.14);padding:.35rem;font-size:.875rem;text-align:start;}'
            + '.ci-table-menu .ctm-head{padding:.35rem .6rem .45rem;font-size:.72rem;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.03em;}'
            + '.ci-table-menu button{display:flex;justify-content:space-between;align-items:center;gap:.75rem;width:100%;padding:.45rem .6rem;border:0;border-radius:6px;background:none;color:#1f2937;text-align:start;}'
            + '.ci-table-menu button:hover,.ci-table-menu button:focus-visible{background:#f3f4f6;outline:0;}'
            + '.ci-table-menu button.is-here{background:#eef2ff;}'
            + '.ci-table-menu .ctm-count{font-size:.75rem;color:#6b7280;white-space:nowrap;}'
            + '.ci-table-menu button.is-full .ctm-count{color:#b45309;}';
        document.head.appendChild(st);
    }

    _closeTableMenu() {
        if (!this._tableMenu) return;
        this._tableMenu.el.remove();
        this._tableMenu = null;
    }

    _placeTableMenu() {
        if (!this._tableMenu) return;
        const btn = [...document.querySelectorAll('#checkinGuestTableBody .ci-table-chip')].find(b => b.dataset.responseId === this._tableMenu.responseId);
        if (!btn) { this._closeTableMenu(); return; }
        const b = btn.getBoundingClientRect();
        const m = this._tableMenu.el;
        const h = m.offsetHeight, w = m.offsetWidth;
        const below = b.bottom + 6 + h <= window.innerHeight || b.top < h + 12;
        m.style.top = (below ? b.bottom + 6 : Math.max(8, b.top - 6 - h)) + 'px';
        const rtl = getComputedStyle(btn).direction === 'rtl';
        m.style.left = Math.min(Math.max(8, rtl ? b.right - w : b.left), window.innerWidth - w - 8) + 'px';
    }

    _openTableMenu(btn) {
        const responseId = btn.dataset.responseId;
        if (this._tableMenu && this._tableMenu.responseId === responseId) { this._closeTableMenu(); return; }
        this._closeTableMenu();
        const r = this.allResponses.find(x => x.id === responseId);
        if (!r) return;
        const name = r.guestName || r.name || this.t('guest');
        const seatedNow = (this.seatTables || []).reduce((n, t) => n + (t.seats || []).filter(s => s && s.responseId === responseId).length, 0);
        const need = Math.max(1, seatedNow || resolveGuestCounts(r).total || 1);
        const el = document.createElement('div');
        el.className = 'ci-table-menu';
        el.setAttribute('role', 'menu');
        el.setAttribute('dir', document.documentElement.dir || 'ltr');
        el.innerHTML = `<div class="ctm-head">${this.escapeHtml(this.t('tableMoveTo').replace('{name}', name))}${need > 1 ? ' ' + this.escapeHtml(this.t('tablePartyOf').replace('{n}', need)) : ''}</div>`
            + this.seatTables.map((t) => {
                const seats = t.seats || [];
                const mine = seats.filter(s => s && s.responseId === responseId).length;
                const cap = parseInt(t.capacity, 10) || 0;
                const full = !!cap && !mine && seats.length + need > cap;
                return `<button type="button" role="menuitem" data-table-id="${this.escapeHtml(t.id)}" class="${mine ? 'is-here' : ''}${full ? ' is-full' : ''}">`
                    + `<span>${mine ? '<i class="bi bi-check2 me-1"></i>' : ''}${this.escapeHtml(String(t.name || '').trim() || 'Table')}</span>`
                    + `<span class="ctm-count">${seats.length}/${cap || '∞'}${full ? ' · ' + this.escapeHtml(this.t('tableFullMark')) : ''}</span></button>`;
            }).join('');
        el.addEventListener('click', (e) => {
            const item = e.target.closest('[data-table-id]');
            if (!item) return;
            if (item.classList.contains('is-here')) { this._closeTableMenu(); return; }
            const t = this.seatTables.find(x => String(x.id) === item.dataset.tableId);
            if (!t) return;
            const cap = parseInt(t.capacity, 10) || 0;
            const free = Math.max(0, cap - (t.seats || []).length);
            if (item.classList.contains('is-full') && !window.confirm(this.t('tableFullConfirm').replace('{table}', t.name || '').replace('{free}', free).replace('{n}', need))) return;
            this._moveParty(responseId, t, item.classList.contains('is-full'), need);
        });
        document.body.appendChild(el);
        this._tableMenu = { el, responseId };
        this._placeTableMenu();
        el.querySelector('button:not(.is-here)')?.focus();
        if (!this._tableMenuBound) {
            this._tableMenuBound = true;
            document.addEventListener('mousedown', (e) => {
                if (this._tableMenu && !this._tableMenu.el.contains(e.target) && !e.target.closest('.ci-table-chip')) this._closeTableMenu();
            }, true);
            document.addEventListener('keydown', (e) => { if (this._tableMenu && e.key === 'Escape') this._closeTableMenu(); });
            window.addEventListener('scroll', () => this._placeTableMenu(), true);
            window.addEventListener('resize', () => this._placeTableMenu());
        }
    }

    async _moveParty(responseId, table, force, need) {
        // Task #888: the list closes the moment a table is picked and the
        // chip shows it straight away. It used to sit there half-faded, shadow
        // and all, until the server answered, and then a toast repeated what
        // the row already said. Put back only if the move fails.
        this._closeTableMenu();
        const chip = [...document.querySelectorAll('#checkinGuestTableBody .ci-table-chip')].find(b => b.dataset.responseId === responseId);
        const label = chip && chip.querySelector('span');
        const before = label ? { text: label.textContent, none: chip.classList.contains('is-none') } : null;
        if (label) { label.textContent = table.name || ''; chip.classList.remove('is-none'); }
        const toast = (m, type) => (window.showToast ? window.showToast(m, type) : window.alert(m));
        try {
            const token = await firebase.auth().currentUser.getIdToken();
            const send = (f) => fetch(`/api/seating/${encodeURIComponent(this.formId)}/move-party`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ responseId, tableId: table.id, ...(f ? { force: true } : {}) })
            });
            let res = await send(force === true);
            let data = await res.json().catch(() => ({}));
            // Filled up since the list was drawn: the same question, then over capacity.
            if (!res.ok && data.tableFull && !force) {
                const cap = parseInt(table.capacity, 10) || 0;
                if (!window.confirm(this.t('tableFullConfirm').replace('{table}', table.name || '').replace('{free}', Math.max(0, cap - (table.seats || []).length)).replace('{n}', need || 1))) throw new Error(data.error || this.t('tableMoveFailed'));
                res = await send(true);
                data = await res.json().catch(() => ({}));
            }
            if (!res.ok || !data.success) throw new Error(data.error || this.t('tableMoveFailed'));
            // The chart listener redraws the row with the new table.
        } catch (err) {
            if (label && before) { label.textContent = before.text; chip.classList.toggle('is-none', before.none); }
            toast(err.message || this.t('tableMoveFailed'), 'error');
        }
    }

    // Undo a check-in from the dashboard (reset arrival status). The realtime
    // listener re-renders the row once the server clears the fields.
    async undoCheckin(responseId, guestName) {
        if (!window.confirm(`${this.t('undoConfirm')} ${guestName}?`)) return;
        try {
            const resp = await fetch('/api/checkin/master-undo', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ formId: this.formId, responseId })
            });
            const data = await resp.json();
            if (!resp.ok || !data.success) throw new Error(data.error || 'undo failed');
            // onSnapshot will refresh the table automatically.
        } catch (err) {
            console.error('Undo check-in failed:', err);
            window.alert(this.t('undoFailed'));
        }
    }

    setupEventListeners() {
        if (this._listenersAttached) return;
        this._listenersAttached = true;

        document.getElementById('checkinGuestSearch')?.addEventListener('input', (e) => {
            this.searchFilter = e.target.value;
            this.renderTable();
        });

        document.getElementById('checkinMasterQrBtn')?.addEventListener('click', () => {
            this.showMasterQrModal();
        });

        // Task #857 r2: opening the scanner or the check-in page with QR Auto
        // on and no tables asks first (add tables / open anyway / cancel).
        for (const id of ['checkinOpenScannerBtn', 'checkinGuestLinkBtn']) {
            document.getElementById(id)?.addEventListener('click', (e) => this._guardDoorOpen(e, e.currentTarget));
        }

        // Undo check-in (delegated — rows re-render on every snapshot).
        document.getElementById('checkinGuestTableBody')?.addEventListener('click', (e) => {
            const chip = e.target.closest('.ci-table-chip');
            if (chip) { this._openTableMenu(chip); return; }
            const btn = e.target.closest('.checkin-undo-btn');
            if (btn) {
                this.undoCheckin(btn.dataset.responseId, btn.dataset.guestName || this.t('guest'));
                return;
            }
            const resend = e.target.closest('.checkin-resend-btn');
            if (resend) {
                this.showResendModal(resend.dataset.responseId, resend.dataset.guestName || this.t('guest'), resend.dataset.guestEmail || '');
            }
        });
    }

    /**
     * "Privacy mode for self-serve check-in" switch under the tab's action
     * buttons — same checkinPrivacyMode flag the builder toggle writes, so a
     * host can flip it from either place. Skipped in demo mode (demo forms are
     * shared fixtures; nothing on the demo dashboard should write).
     */
    renderPrivacyToggle(formData) {
        if (!formData || window.isDemoMode) return;
        // Task #600: the row reads buttons | scanner | counts+progress, so the
        // switch goes at the END of it rather than being wedged in after the
        // first two buttons — which is what pushed the counts onto a second
        // line and left the progress bar stranded on the right.
        // Task #829: under the toolbar on a line of its own, not inside the
        // right-hand button group. Anchored on the toolbar (or, before the
        // layout runs, the Master QR button's row) - the old anchor,
        // .checkin-primary-actions, exists only on the English page, so the
        // switch never appeared on the six translated dashboards.
        const row = document.querySelector('#checkinMainContent .ci-toolbar')
            || document.getElementById('checkinMasterQrBtn')?.closest('#checkinMainContent > div');
        if (!row) return;
        let wrap = document.getElementById('checkinPrivacyToggleWrap');
        if (!wrap) {
            wrap = document.createElement('div');
            wrap.id = 'checkinPrivacyToggleWrap';
            // One line, not three (task #600). The label and its note sit side
            // by side and wrap only when they have to, so the control bar keeps
            // its height instead of growing a stacked paragraph.
            wrap.className = 'form-check form-switch mt-1 mb-0 d-flex align-items-center flex-wrap';
            wrap.style.gap = '0.4rem';
            wrap.style.rowGap = '0.15rem';
            wrap.innerHTML = `
                <input class="form-check-input mt-0" type="checkbox" id="checkinPrivacyToggle">
                <label class="form-check-label mb-0" for="checkinPrivacyToggle">${this.t('privacyLabel')}</label>
                <span class="text-muted" style="font-size:12.5px;">${this.t('privacyDesc')}</span>`;
            row.appendChild(wrap);
            const cb = wrap.querySelector('#checkinPrivacyToggle');
            cb.addEventListener('change', async () => {
                const next = cb.checked;
                cb.disabled = true;
                try {
                    await firebase.firestore().collection('rsvp_forms').doc(this.formId)
                        .update({ checkinPrivacyMode: next });
                    // No toast (task #601): the switch has already moved, which
                    // is the confirmation. A FAILURE still speaks up below —
                    // there the switch springs back, and silence would leave the
                    // host believing a setting that did not save.
                } catch (err) {
                    console.error('Privacy mode save failed:', err);
                    cb.checked = !next;
                    this._toast(this.t('privacySaveFailed'), 'error');
                } finally {
                    cb.disabled = false;
                }
            });
        }
        wrap.querySelector('#checkinPrivacyToggle').checked = formData.checkinPrivacyMode === true;
    }

    /**
     * Carry a just-saved guest email into what is already on screen (task #613
     * round 2): the cached row the table renders from, and the resend button's
     * own data attribute, which is what the NEXT open of this dialog reads.
     *
     * The Firestore listener will deliver the same value a moment later and
     * overwrite both with identical data — this only closes the gap in
     * between, which is exactly when a host re-opens the dialog to check.
     */
    _applySavedGuestEmail(responseId, email) {
        const clean = String(email || '').trim();
        if (!responseId || !clean) return;
        const row = (this.allResponses || []).find(r => r && r.id === responseId);
        if (row && !row.email) row.email = clean;
        document.querySelectorAll(`.checkin-resend-btn[data-response-id="${responseId}"]`)
            .forEach((btn) => { btn.dataset.guestEmail = clean; });
    }

    _toast(message, type) {
        if (typeof window.showToast === 'function') window.showToast(message, type);
        else window.alert(message);
    }

    async _resendApi(body) {
        const token = await firebase.auth().currentUser?.getIdToken();
        if (!token) throw new Error('Not signed in');
        const resp = await fetch('/api/checkin/resend-pass', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ formId: this.formId, ...body })
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data.success) throw new Error(data.error || 'Request failed');
        return data;
    }

    // Free bulk resend: emails every eligible attending guest their pass again.
    async resendAllPasses(btn) {
        if (!this.formId) return;
        if (!window.confirm(this.t('resendAllConfirm'))) return;
        const original = btn.innerHTML;
        btn.disabled = true;
        btn.innerHTML = `<span class="spinner-border spinner-border-sm me-1"></span>${this.t('resendAllBtn')}`;
        try {
            const data = await this._resendApi({ all: true });
            if (data.sent === 0 && data.noEmail > 0) {
                this._toast(this.t('resendAllNone'), 'warning');
            } else {
                const extra = data.noEmail > 0 ? ` (${data.noEmail} ${this.t('resendAllNoEmail')})` : '';
                this._toast(`${this.t('resendAllDone')}: ${data.sent}${extra}`, 'success');
            }
        } catch (err) {
            console.error('Bulk pass resend failed:', err);
            this._toast(`${this.t('resendFailed')}: ${err.message}`, 'error');
        } finally {
            btn.disabled = false;
            btn.innerHTML = original;
        }
    }

    // Per-guest resend modal (task #419): email the pass, or share the pass
    // link over the host's own WhatsApp / clipboard — all free.
    async showResendModal(responseId, guestName, guestEmail) {
        document.getElementById('checkinResendModal')?.remove();

        const isRtl = ['he', 'ar'].includes(this.lang);
        const modalEl = document.createElement('div');
        modalEl.className = 'modal fade';
        modalEl.id = 'checkinResendModal';
        modalEl.tabIndex = -1;
        if (isRtl) modalEl.setAttribute('dir', 'rtl');
        modalEl.innerHTML = `
            <div class="modal-dialog modal-dialog-centered modal-sm">
                <div class="modal-content border-0 shadow">
                    <div class="modal-header border-0 pb-0">
                        <h6 class="modal-title fw-bold"><i class="bi bi-qr-code me-2"></i>${this.t('resendTitle')}</h6>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body pt-2">
                        <!-- Task #635: the line "Send this guest their personal
                             check-in QR code again" was removed. The dialog is
                             titled "Resend QR Pass" and the buttons say Email QR
                             Pass / Copy pass link — the sentence restated all of
                             it and pushed the useful lines (who, what it costs,
                             where it is going) down the dialog. -->
                        <div class="fw-semibold mb-2">${this.escapeHtml(guestName)}</div>
                        <p class="text-muted small mb-3"><i class="bi bi-gift me-1"></i>${this.t('resendFree')}</p>
                        <div class="d-grid gap-2">
                            ${guestEmail ? `
                            <!-- Task #629: the address used to be a second line
                                 INSIDE the button, which read as part of the label
                                 and pushed the button out of shape on a long one.
                                 It is its own line above now, so the button is a
                                 clean two-word action and the destination is a
                                 fact you can check before pressing it. -->
                            <div class="checkin-resend-to small text-muted">
                                ${this.t('resendSendingTo')}
                                <span class="checkin-resend-to-addr" dir="ltr" title="${this.escapeHtml(guestEmail)}">${this.escapeHtml(guestEmail)}</span>
                            </div>
                            <button type="button" class="btn btn-primary btn-sm" id="checkinResendEmailBtn">
                                <i class="bi bi-envelope me-1"></i>${this.t('resendEmailBtn')}
                            </button>` : `
                            <!-- Task #613: a guest with no address used to get a dead
                                 button and a "share the link instead" line, which meant
                                 leaving the dialog, finding the response, adding the
                                 email and coming back. Type it here instead: it is saved
                                 onto the guest and the pass goes out in the same click. -->
                            <div class="text-muted small">${this.t('resendAddEmailHint')}</div>
                            <input type="email" class="form-control form-control-sm" id="checkinResendEmailInput"
                                   dir="ltr" autocomplete="email" inputmode="email"
                                   placeholder="${this.escapeHtml(this.t('resendAddEmailPlaceholder'))}"
                                   aria-label="${this.escapeHtml(this.t('resendAddEmailBtn'))}">
                            <div class="text-danger small d-none" id="checkinResendEmailError"></div>
                            <button type="button" class="btn btn-primary btn-sm" id="checkinResendEmailBtn" disabled>
                                <i class="bi bi-envelope me-1"></i>${this.t('resendAddEmailBtn')}
                            </button>`}
                            <button type="button" class="btn btn-success btn-sm d-none" id="checkinResendWaBtn">
                                <i class="bi bi-whatsapp me-1"></i>${this.t('resendWhatsAppBtn')}
                            </button>
                            <button type="button" class="btn btn-outline-secondary btn-sm d-none" id="checkinResendCopyBtn">
                                <i class="bi bi-clipboard me-1"></i>${this.t('resendCopyBtn')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>`;
        // A long address must WRAP rather than stretch the dialog sideways, and
        // it reads as a value, not as prose — hence the medium weight and the
        // darker colour against the muted label beside it (task #629).
        if (!document.getElementById('checkin-resend-styles')) {
            const st = document.createElement('style');
            st.id = 'checkin-resend-styles';
            st.textContent = '.checkin-resend-to{margin:-0.25rem 0 0.35rem;line-height:1.45}'
                + '.checkin-resend-to-addr{display:inline-block;font-weight:500;color:#374151;'
                + 'word-break:break-all;max-width:100%}';
            document.head.appendChild(st);
        }
        document.body.appendChild(modalEl);
        const modal = new bootstrap.Modal(modalEl);
        modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove());
        modal.show();

        // Fetch the pass link (no email is sent) to light up share options.
        let passUrl = null, guestPhone = null, eventTitle = '';
        try {
            const data = await this._resendApi({ responseId, linkOnly: true });
            passUrl = data.passUrl;
            guestPhone = data.guestPhone;
            eventTitle = data.eventTitle || '';
        } catch (err) {
            console.warn('Pass link fetch failed:', err);
        }
        if (passUrl) {
            const copyBtn = modalEl.querySelector('#checkinResendCopyBtn');
            copyBtn.classList.remove('d-none');
            copyBtn.addEventListener('click', async () => {
                try {
                    await navigator.clipboard.writeText(passUrl);
                    this._toast(this.t('resendCopied'), 'success');
                } catch (e) { /* clipboard denied — nothing to break */ }
            });
            const phoneDigits = String(guestPhone || '').replace(/[^\d]/g, '');
            if (phoneDigits.length >= 8) {
                const waBtn = modalEl.querySelector('#checkinResendWaBtn');
                waBtn.classList.remove('d-none');
                waBtn.addEventListener('click', () => {
                    const text = encodeURIComponent(`${this.t('resendWhatsAppMsg')} ${eventTitle}: ${passUrl}`);
                    window.open(`https://wa.me/${phoneDigits}?text=${text}`, '_blank', 'noopener');
                });
            }
        }

        // One send path for both shapes: with an address on file it just sends;
        // without one it also carries what the host typed, which the server
        // validates, writes onto the guest, and then sends to (task #613).
        const emailBtn = modalEl.querySelector('#checkinResendEmailBtn');
        const emailInput = modalEl.querySelector('#checkinResendEmailInput');
        const emailError = modalEl.querySelector('#checkinResendEmailError');
        const label = () => (guestEmail ? this.t('resendEmailBtn') : this.t('resendAddEmailBtn'));
        const looksLikeEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || '').trim());

        if (emailInput) {
            // The button stays out of reach until there is something to send to,
            // so the host never presses it and waits for a failure.
            emailInput.addEventListener('input', () => {
                emailBtn.disabled = !looksLikeEmail(emailInput.value);
                emailError.classList.add('d-none');
            });
            emailInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' && !emailBtn.disabled) { e.preventDefault(); emailBtn.click(); }
            });
            setTimeout(() => { try { emailInput.focus(); } catch (e) {} }, 250);
        }

        emailBtn.addEventListener('click', async () => {
            const typed = emailInput ? String(emailInput.value || '').trim() : '';
            if (emailInput && !looksLikeEmail(typed)) {
                emailError.textContent = this.t('resendEmailInvalid');
                emailError.classList.remove('d-none');
                return;
            }
            emailBtn.disabled = true;
            if (emailInput) emailInput.disabled = true;
            emailBtn.innerHTML = `<span class="spinner-border spinner-border-sm me-1"></span>${label()}`;
            try {
                const data = await this._resendApi({ responseId, ...(typed ? { email: typed } : {}) });
                if (data && data.emailSaved) {
                    this._toast(this.t('resendEmailSaved'), 'success');
                    // Show it NOW rather than when the live listener catches up.
                    // The address is saved either way, but re-opening this dialog
                    // before the snapshot lands showed an empty box again, which
                    // reads as "it didn't save, type it again" (task #613 r2).
                    // Cosmetic — never allowed to turn a send that WORKED into
                    // the failure branch below.
                    try { this._applySavedGuestEmail(responseId, typed); } catch (_) { /* ignore */ }
                }
                this._toast(`${this.t('resendSent')} — ${guestName}`, 'success');
                modal.hide();
                // No manual redraw needed: the table is driven by an onSnapshot
                // listener on the responses collection, so the saved address
                // reaches the row on its own.
            } catch (err) {
                console.error('Pass resend failed:', err);
                if (emailInput) {
                    emailError.textContent = err.message || this.t('resendFailed');
                    emailError.classList.remove('d-none');
                    emailInput.disabled = false;
                } else {
                    this._toast(`${this.t('resendFailed')}: ${err.message}`, 'error');
                }
                emailBtn.disabled = false;
                emailBtn.innerHTML = `<i class="bi bi-envelope me-1"></i>${label()}`;
            }
        });
    }

    /**
     * The Master QR sign. `formId` is the event's: the Seating tab opens it too
     * (task #1024), and this.formId is only set once the Check-In tab has been
     * opened, so from Seating the button used to do nothing at all.
     */
    async showMasterQrModal(formId = this.formId) {
        if (!formId) return;
        this._masterQrFormId = formId;

        // Ensure qrcode library is loaded
        if (typeof qrcode === 'undefined') {
            await new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js';
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            });
        }

        // Create or get modal
        let modalEl = document.getElementById('masterQrModal');
        if (!modalEl) {
            const isRtl = ['he', 'ar'].includes(this.lang);
            modalEl = document.createElement('div');
            modalEl.className = 'modal fade';
            modalEl.id = 'masterQrModal';
            modalEl.tabIndex = '-1';
            if (isRtl) modalEl.setAttribute('dir', 'rtl');
            modalEl.innerHTML = `
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content border-0 shadow">
                        <div class="modal-header border-0 pb-0">
                            <h5 class="modal-title fw-bold"><i class="bi bi-qr-code me-2"></i>${this.t('masterQrTitle')}</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body text-center pt-3 pb-4">
                            <p class="text-muted small mb-4">${this.t('masterQrDesc')}</p>

                            <div class="p-4 bg-light rounded-4 d-inline-block position-relative mb-3" style="border: 1px dashed #ccc;">
                                <div id="masterQrContainer" style="background: white; padding: 15px; border-radius: 10px;">
                                    <div class="spinner-border text-primary" role="status"></div>
                                </div>
                            </div>

                            <div class="d-flex justify-content-center gap-2 mt-2">
                                <a id="masterQrLink" target="_blank" href="#" class="btn btn-outline-secondary btn-sm">
                                    <i class="bi bi-box-arrow-up-right"></i> ${this.t('masterQrOpen')}
                                </a>
                                <button class="btn btn-primary btn-sm" onclick="downloadMasterQR()">
                                    <i class="bi bi-download me-1"></i>${this.t('masterQrDownload')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            `;
            document.body.appendChild(modalEl);

            // Add download handler to window
            window.downloadMasterQR = () => {
                const img = document.querySelector('#masterQrContainer img');
                if (img && img.src) {
                    const link = document.createElement('a');
                    link.href = img.src;
                    link.download = `Checkin-Sign-${this._masterQrFormId}.png`;
                    link.click();
                }
            };
        }

        // Build the link
        const hostUrl = window.location.origin;
        const lookupUrl = `${hostUrl}/guest-lookup.html?formId=${encodeURIComponent(formId)}`;

        // Use Bootstrap Modal API
        const bsModal = new bootstrap.Modal(modalEl);
        bsModal.show();

        // Generate QR Code
        setTimeout(() => {
            const container = document.getElementById('masterQrContainer');
            container.innerHTML = ''; // clear loading

            try {
                const qr = qrcode(0, 'M');
                qr.addData(lookupUrl);
                qr.make();

                // create img element manually for better compatibility
                const imgHtml = qr.createImgTag(5, 10);
                container.innerHTML = imgHtml;
                container.querySelector('img').style.maxWidth = '100%';

                document.getElementById('masterQrLink').href = lookupUrl;
            } catch (err) {
                console.error("QR Code Error:", err);
                container.innerHTML = '<p class="text-danger small">Error generating QR code</p>';
            }
        }, 150);
    }

    destroy() {
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text || '';
        return div.innerHTML;
    }
}

// Create global instance
window.dashboardCheckin = new DashboardCheckin();

// Listen for tab activation and form changes
document.addEventListener('DOMContentLoaded', () => {
    document.getElementById('checkin-tab')?.addEventListener('shown.bs.tab', () => {
        const formId = typeof getCurrentFormId === 'function' ? getCurrentFormId() : null;
        if (formId) {
            window.dashboardCheckin.init(formId);
        } else {
            window.dashboardCheckin.showNoFormError();
        }
    });

    // Re-init on form change if check-in tab is active
    document.getElementById('form-select')?.addEventListener('change', (e) => {
        if (document.getElementById('checkin-tab')?.classList.contains('active')) {
            window.dashboardCheckin.init(e.target.value);
        }
    });
});
